import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, Play, FolderLock, Loader2 } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const OUTCOME = {
  allowed: { label: "Consentito", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  denied_forbidden: { label: "Negato (altro salone)", cls: "bg-red-50 text-red-700 border-red-200" },
  denied_anonymous: { label: "Negato (anonimo)", cls: "bg-orange-50 text-orange-800 border-orange-200" },
  not_found: { label: "Non trovato", cls: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  error: { label: "Errore", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

export const IsolationTestPanel = () => {
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/security/isolation-tests", { params: { limit: 5 } });
      setRuns(data.runs || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/security/isolation-test");
      toast[data.passed ? "success" : "error"](
        data.passed ? "Test isolamento superato" : `Test fallito: ${data.failed.join(", ")}`
      );
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  const last = runs[0];
  return (
    <div className="rounded-xl border border-[var(--mp-border,#e5e7eb)] bg-white p-4 space-y-3" data-testid="isolation-test-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          {last?.passed === false ? <ShieldX className="h-4 w-4 text-red-600" /> : <ShieldCheck className="h-4 w-4 text-emerald-600" />}
          Test automatico isolamento file
          <span className="text-xs font-normal text-neutral-500">ogni giorno alle 05:00 · solo dati di prova</span>
        </div>
        <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="isolation-test-run-btn">
          {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />} Esegui ora
        </Button>
      </div>
      {last ? (
        <div className="text-sm" data-testid="isolation-test-last">
          Ultimo: <span className="font-medium">{fmt(last.at)}</span> ·{" "}
          <span className={last.passed ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
            {last.passed ? "SUPERATO" : "FALLITO"}
          </span>{" "}
          · {last.checks.length} controlli · {last.duration_ms} ms
          {last.failed?.length > 0 && <div className="text-red-700 mt-1">Falliti: {last.failed.join(", ")}</div>}
          {last.error && <div className="text-red-700 mt-1">Errore: {last.error}</div>}
          <button className="text-xs underline text-neutral-500 mt-1" onClick={() => setOpen((v) => !v)} data-testid="isolation-test-toggle-details">
            {open ? "Nascondi dettagli" : "Mostra dettagli"}
          </button>
          {open && (
            <ul className="mt-2 space-y-0.5 text-xs">
              {last.checks.map((c) => (
                <li key={c.name} className={c.ok ? "text-neutral-700" : "text-red-700 font-medium"}>
                  {c.ok ? "✓" : "✗"} {c.name} — atteso {String(c.expected)}, ottenuto {String(c.got)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="text-sm text-neutral-500">Nessuna esecuzione registrata.</div>
      )}
    </div>
  );
};

export const FileAccessPanel = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [outcome, setOutcome] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/security/file-access", {
        params: { q: q || undefined, outcome: outcome || undefined, limit: 150 },
      });
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [q, outcome]);

  useEffect(() => {
    load();
  }, [load]);

  const c = data?.last_24h || {};
  const events = data?.events || [];
  return (
    <div className="space-y-3" data-testid="file-access-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          <FolderLock className="h-4 w-4" /> Accessi ai file privati
          <span className="text-xs font-normal text-neutral-500">ultime 24 h: {c.allowed || 0} consentiti · {(c.denied_forbidden || 0) + (c.denied_anonymous || 0)} negati · conservazione 90 giorni</span>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Cerca email, salone, file…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-56" data-testid="file-access-search" />
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="h-9 rounded-md border border-neutral-200 bg-white px-2 text-sm" data-testid="file-access-outcome-filter">
            <option value="">Tutti gli esiti</option>
            {Object.entries(OUTCOME).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[var(--mp-border,#e5e7eb)] bg-white">
        <table className="w-full text-sm" data-testid="file-access-table">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
            <tr>
              <th className="px-3 py-2">Quando</th>
              <th className="px-3 py-2">Esito</th>
              <th className="px-3 py-2">Utente</th>
              <th className="px-3 py-2">Salone utente</th>
              <th className="px-3 py-2">Salone risorsa</th>
              <th className="px-3 py-2">Risorsa</th>
              <th className="px-3 py-2">Endpoint</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-500">Nessun accesso registrato.</td></tr>
            )}
            {events.map((e) => {
              const m = OUTCOME[e.outcome] || { label: e.outcome, cls: "bg-neutral-100 text-neutral-700 border-neutral-200" };
              return (
                <tr key={e.id} className="border-t border-neutral-100" data-testid={`file-access-${e.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-neutral-600">{fmt(e.at)}</td>
                  <td className="px-3 py-2"><span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${m.cls}`}>{m.label}</span></td>
                  <td className="px-3 py-2">{e.email || <span className="text-neutral-400">anonimo</span>}<span className="text-xs text-neutral-500"> {e.role ? `(${e.role})` : ""}</span></td>
                  <td className="px-3 py-2 text-neutral-600">{e.user_salon || "—"}</td>
                  <td className="px-3 py-2 text-neutral-600">{e.resource_salon || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-600 max-w-[280px] truncate" title={e.resource}>{e.resource}</td>
                  <td className="px-3 py-2 text-xs text-neutral-500">{e.endpoint}{e.mode ? ` · ${e.mode}` : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
